import { useEffect, useMemo, useState } from 'react'

import { BaseModal2 } from '@components/modals/BaseModal2'
import { Breadcrumb } from '@components/shared/Breadcrumb'
import { budgetPreviews } from './components/BudgetPreviews'
import { BudgetPreviewsWrapper } from '@components/shared/BudgetPreviewsWrapper'
import { Button } from '@components/shared/Button'
import { EditBonusModal } from './components/EditBonusModal'
import { emotionTheme } from '@styles/theme'
import { getPagePermissions } from '@hooks/useUser/provider'
import { PageIds } from '@hooks/useLayout/types'
import { parseBonusPreviewsGrid } from './helpers/parseBonusPreviewsGrid'
import { PreviewsStatus } from '@components/shared/BudgetPreviewsStatus/types'
import { PreviewWrapper } from '@components/shared/PreviewWrapper'
import { ReturnIcon } from '@assets/svgs/ReturnIcon'
import { ReturnModal } from '@components/modals/ReturnModal'
import { Slider } from '@components/shared/Slider'
import { Table } from '@components/shared/Table'
import { Title } from '@components/shared/Title'
import { useBonus } from './context'
import { useMediaQuery } from '@mui/material'
import { useUser } from '@hooks/useUser'
// import { PROFILE_GU } from '@pages/Preview/helpers/previewGridValidationActions'
import { WarningBanner } from '@components/shared/WarningBanner'
import { WarningIcon } from '@phosphor-icons/react'

import type { ClientModalProps } from './types'
import type { BonusPreviewProps } from './context/types'
import type { PermissionsItemProps } from '@pages/AccessProfile/context/types'

import * as S from './styles'

export const BonusModal = ({ canEdit = true, open, onClose, ...rest }: ClientModalProps) => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)

  const { filiData, userPermissionsData } = useUser()
  // const isGUProfile = userPermissionsData?.profile?.name === PROFILE_GU
  const {
    bonusData,
    isBonusLoading,
    getBonus,
    isApprovalWorkflowBonusLoading,
    approvalWorkflowBonus,
    isReleasePreviewBonusLoading,
    releasePreviewBonus,
    isReversePreviewBonusLoading,
    reversePreviewBonus
  } = useBonus()

  const [openEditBonusModal, setOpenEditBonusModal] = useState(false)
  const [editBonusData, setEditBonusData] = useState<BonusPreviewProps | null>(null)
  const [openApprovalWorkflowModal, setOpenApprovalWorkflowModal] = useState(false)
  const [openReleasePreviewModal, setOpenReleasePreviewModal] = useState(false)
  const [openReversePreviewModal, setOpenReversePreviewModal] = useState(false)
  const [permissions, setPermissions] = useState<PermissionsItemProps | null>(null)
  const canEditFromBonusPreview = bonusData?.canEdit === true
  const canEditPage = canEdit && canEditFromBonusPreview && permissions?.canEdit === true

  const hasInProgressPreview = useMemo(() => {
    return bonusData?.previews?.some((item) => item.previewStatus.code === PreviewsStatus.IN_PROGRESS)
  }, [bonusData?.previews])

  const showReleaseOrRevertButtons = !!bonusData?.allowReleasePreview || !!bonusData?.allowRevertPreview

  const handleOpenEditBonus = (data: BonusPreviewProps) => {
    setOpenEditBonusModal(true)
    setEditBonusData(data)
  }

  const handleInitialActionsFromScreen = () => {
    const payload = { filiCode: filiData?.filiCode!, competenceDate: filiData?.startPeriod! }

    getBonus(payload)
  }

  useEffect(() => {
    !!filiData?.filiCode && !!open && handleInitialActionsFromScreen()
  }, [open, filiData])

  useEffect(() => {
    const pagePermissions = getPagePermissions(PageIds.CONSUMPTION)
    setPermissions(pagePermissions)
  }, [userPermissionsData])

  return (
    <>
      <BaseModal2
        open={open}
        width={isMobile ? 'calc(100% - 20px)' : '96.5%'}
        heightContainer="auto"
        maxHeightContainer={isMobile ? 'calc(100% - 20px)' : '95%'}
        onClose={onClose}
        headerIcon={
          <Breadcrumb
            breadcrumbData={[
              { label: 'Gestão Financeira' },
              { label: `Bonificação / ${filiData?.filiCode} - ${filiData?.filiName}` }
            ]}
          />
        }
        footer={
          showReleaseOrRevertButtons
            ? undefined
            : {
                cancelButtonText: 'Voltar',
                cancelButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5,
                confirmButtonDisabled: isBonusLoading || !hasInProgressPreview,
                cancelButtonDisabled: isBonusLoading
              }
        }
        {...rest}
      >
        <WarningBanner message={bonusData?.corporateFoodSegmentMessage} marginTop={0} style={{ marginBottom: 24 }} />

        <Title label="Bonificação" marginTop={0} />

        <BudgetPreviewsWrapper>
          {isMobile ? (
            <Slider items={budgetPreviews({ data: bonusData })} />
          ) : (
            <PreviewWrapper>{budgetPreviews({ data: bonusData })}</PreviewWrapper>
          )}

          <Table
            data={parseBonusPreviewsGrid({
              data: bonusData?.previews || [],
              actionsDisabled: !canEditPage,
              onEdit: handleOpenEditBonus
            })}
            isLoading={isBonusLoading}
          />
        </BudgetPreviewsWrapper>

        <S.Buttons>
          {showReleaseOrRevertButtons && (
            <Button
              width={isMobile ? '100%' : 190}
              colorType="outlineBlue"
              onClick={() => onClose(false)}
              disabled={isBonusLoading}
            >
              Voltar
            </Button>
          )}

          {bonusData?.allowRevertPreview && (
            <Button
              width={isMobile ? '100%' : 190}
              colorType="outlineBlue"
              onClick={() => setOpenReversePreviewModal(true)}
            >
              Reverter Prévia
            </Button>
          )}

          {canEdit && (
            <>
              {bonusData?.allowReleasePreview && (
                <Button
                  width={isMobile ? '100%' : 190}
                  colorType="solidBlue"
                  onClick={() => setOpenReleasePreviewModal(true)}
                >
                  Liberar Prévia
                </Button>
              )}
            </>
          )}
        </S.Buttons>
      </BaseModal2>

      <EditBonusModal
        data={editBonusData}
        open={openEditBonusModal}
        onClose={setOpenEditBonusModal}
        canEdit={canEditPage}
      />

      <ReturnModal
        open={openApprovalWorkflowModal}
        headerIcon={<ReturnIcon />}
        headerTitle={`Prévia Bonificação - ${bonusData?.currentPreviewNumber}ª semana`}
        title="Você tem certeza de que deseja enviar a prévia?"
        message="A prévia será enviada para aprovação."
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenApprovalWorkflowModal}
        isLoading={isApprovalWorkflowBonusLoading}
        onConfirm={() =>
          approvalWorkflowBonus({
            filiCode: filiData?.filiCode!,
            competenceDate: filiData?.startPeriod!
          }).then((isSuccess) => isSuccess && setOpenApprovalWorkflowModal(false))
        }
      />

      <ReturnModal
        open={openReleasePreviewModal}
        headerIcon={<WarningIcon size={24} weight="bold" />}
        headerTitle="Liberar Prévia"
        message={`Tem certeza de que deseja liberar a ${bonusData?.currentPreviewNumber}ª Prévia de Bonificação?`}
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenReleasePreviewModal}
        isLoading={isReleasePreviewBonusLoading}
        onConfirm={() => {
          if (!canEditPage) {
            return
          }

          releasePreviewBonus({
            filiCode: filiData?.filiCode!,
            competenceDate: filiData?.startPeriod!
          }).then((isSuccess) => isSuccess && setOpenReleasePreviewModal(false))
        }}
      />

      <ReturnModal
        open={openReversePreviewModal}
        headerIcon={<WarningIcon size={24} weight="bold" />}
        headerTitle="Reverter Prévia"
        message={`Tem certeza de que deseja reverter a ${bonusData?.currentPreviewNumber}ª Prévia de Bonificação?`}
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenReversePreviewModal}
        isLoading={isReversePreviewBonusLoading}
        onConfirm={() => {
          reversePreviewBonus({
            filiCode: filiData?.filiCode!,
            competenceDate: filiData?.startPeriod!
          }).then((isSuccess) => isSuccess && setOpenReversePreviewModal(false))
        }}
      />
    </>
  )
}
